import { captureRemixErrorBoundaryError, withSentry } from "@sentry/remix";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLocation,
  useNavigate,
  useLoaderData,
  useRouteError,
  isRouteErrorResponse,
} from "@remix-run/react";
import type { LinksFunction, LoaderFunctionArgs } from "@remix-run/node";
import { json, redirect } from "@remix-run/node";
import { useEffect } from "react";
import { getUserSession } from "~/utils/session.server";
import { findDocumentByField } from "~/utils/firestore.server";
import { isAdminUser } from "~/utils/admin";

import "./tailwind.css";
import prizoLogo from "./assets/prizo-logo.svg";
import Layout from "./components/Layout";
import RTLWrapper from "./components/RTLWrapper";
import { I18nProvider, GamesLanguageProvider, initializeGamesLanguage, getLocaleFromCookie } from "./i18n/i18n";

export const links: LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Space+Grotesk:wght@500;600;700&display=swap",
  },
];

export async function loader({ request }: LoaderFunctionArgs) {
  const __t0 = Date.now();
  const url = new URL(request.url);
  const pathname = url.pathname;
  const fullUrl = request.url;

  // Locale chosen by the user. The cookie is read first so SSR renders the right
  // language/direction on first paint (avoids an English -> Hebrew flash); for
  // logged-in users the account setting (clientDoc.locale, loaded below) wins so
  // the choice follows them across devices.
  let locale = getLocaleFromCookie(request.headers.get('cookie'));

  // Block .data resource route requests at root level to prevent infinite loops
  if (pathname.includes('.data') || pathname.endsWith('.data') || fullUrl.includes('.data')) {
    console.error(`🚫 BLOCKED at root loader: Request to ${pathname} (${fullUrl}) - returning 404`);
    throw new Response("Resource route blocked", { status: 404 });
  }

  // Skip authentication for API routes - they handle their own auth
  if (pathname.startsWith('/api/')) {
    return json({
      ENV: {
        NODE_ENV: process.env.NODE_ENV,
        GA4_MEASUREMENT_ID: process.env.GA4_MEASUREMENT_ID || null,
      },
      user: null,
      isPublicRoute: true,
      isNoLayoutRoute: true,
      hasAccountIssues: false,
      locale,
    });
  }

  // Define routes that don't require authentication
  const publicRoutes = [
    '/login',
    '/register',
    '/landing',
    '/privacy',
    '/terms',
    '/faq',
    '/plinko',
    '/basketball',
    '/pick-a-box',
    '/spin-the-wheel',
    '/hit-the-target',
    '/lucky-dice',
    '/speed-tapper',
    '/color-match-challenge',
    '/claw-machine',
    '/scratch-reveal',
    '/break-ice-cube',
    '/light-the-bulb',
    '/billing-callback',
    // SEO game pages
    '/seo/spin-the-wheel',
    '/seo/scratch-reveal',
    '/seo/pick-a-box',
    '/seo/plinko',
    '/seo/claw-machine',
    '/seo/basketball',
    '/seo/hit-the-target',
    '/seo/lucky-dice',
    '/seo/break-ice-cube',
    // SEO solution pages
    '/solutions/reduce-cart-abandonment',
    '/solutions/increase-aov',
    '/solutions/post-purchase-engagement',
    '/solutions/customer-retention',
    '/solutions/gamified-checkout',
    '/solutions/email-list-growth',
    // SEO comparison pages
    '/compare/wheelio',
    '/compare/privy',
    '/compare/spin-wheel-apps',
    // SEO industry pages
    '/for/fashion',
    '/for/beauty',
    '/for/food-beverage',
    '/for/electronics',
    // SEO blog articles
    '/blog/how-to-reduce-cart-abandonment',
    '/blog/how-to-increase-average-order-value',
    '/blog/customer-retention-strategies',
    '/blog/ecommerce-gamification',
    '/blog/spin-the-wheel-popup-best-practices',
  ];
  const isSeoOrSitemapRoute =
    // Hub (category index) pages — bare paths, no trailing slash.
    pathname === '/seo' ||
    pathname === '/solutions' ||
    pathname === '/compare' ||
    pathname === '/for' ||
    pathname === '/blog' ||
    pathname.startsWith('/seo/') ||
    pathname.startsWith('/solutions/') ||
    pathname.startsWith('/compare/') ||
    pathname.startsWith('/for/') ||
    pathname.startsWith('/blog/') ||
    pathname === '/sitemap.xml' ||
    pathname === '/llms.txt';

  // Define routes that require authentication but don't use layout (no sidebar)
  const noLayoutRoutes = ['/onboarding'];

  let isPublicRoute =
    publicRoutes.some(route => pathname === route) || isSeoOrSitemapRoute;
  const isNoLayoutRoute = noLayoutRoutes.some(route => pathname === route);

  // Get user from session
  const user = await getUserSession(request);

  // Extract shop param from query string
  const shop = url.searchParams.get("shop");

  // Shopify embedded-app install flow: a logged-out hit on "/" carrying a shop
  // param must still go to install (handle before "/" is treated as the public
  // marketing landing).
  if (!user && shop && pathname === "/") {
    const installParams = new URLSearchParams(url.searchParams);
    installParams.set("shop", shop);
    return redirect(`/api/install?${installParams.toString()}`);
  }

  // The homepage is public for logged-out visitors (renders the marketing
  // landing). For authenticated users it stays private (renders the dashboard
  // with the app Layout), so this depends on auth.
  const isHomePublic = pathname === "/" && !user;
  if (isHomePublic) {
    isPublicRoute = true;
  }

  // Check for account issues (only for authenticated users on non-public routes)
  let hasAccountIssues = false;
  let clientDoc: Record<string, any> | null = null;
  if (user && user.email && !isPublicRoute) {
    try {
      clientDoc = await findDocumentByField('clients', 'email', user.email);
      if (clientDoc && clientDoc.confirmationUrl && clientDoc.subscriptionStatus?.toUpperCase() === 'PENDING') {
        hasAccountIssues = true;
      }
    } catch (error) {
      console.error('Error checking account issues:', error);
      // Don't fail the request if account check fails
    }
  }

  // Account-level language preference wins over the cookie for logged-in users.
  if (clientDoc?.locale && (['en', 'es', 'he'] as const).includes(clientDoc.locale)) {
    locale = clientDoc.locale;
  }

  const shouldPromptCheckoutExtension = Boolean(
    user &&
    !isPublicRoute &&
    clientDoc &&
    !clientDoc.lastCheckoutThankYouRenderAt
  );

  // Redirect logic
  if (!user) {
    // User is not authenticated
    if (!isPublicRoute) {
      // Handle Shopify installation flow: redirect root requests with shop param to install endpoint
      if (shop && pathname === "/") {
        const installParams = new URLSearchParams(url.searchParams);
        // Ensure shop parameter is preserved
        installParams.set("shop", shop);
        const installUrl = `/api/install?${installParams.toString()}`;
        return redirect(installUrl);
      }

      // Redirect to login for other protected routes (including no-layout routes)
      // If shop param exists, append it to the login URL
      const loginUrl = shop ? `/login?shop=${encodeURIComponent(shop)}` : '/login';
      return redirect(loginUrl);
    }
  } else {
    // User is authenticated - check if onboarding is complete
    // Skip check for public routes, onboarding route itself, and admin users
    if (!isPublicRoute && pathname !== '/onboarding' && user.email && !isAdminUser(user.email)) {
      // Check if onboardingCompleted exists and is true
      // If clientDoc doesn't exist OR onboardingCompleted is not true, redirect to onboarding
      if (!clientDoc || !clientDoc.onboardingCompleted) {
        // Preserve shop parameter if present for Shopify embedded app flow
        const onboardingUrl = shop ? `/onboarding?shop=${encodeURIComponent(shop)}` : '/onboarding';
        return redirect(onboardingUrl);
      }
    }
  }

  // PostHog client config (only in production when key is set)
  const posthogKey = process.env.NODE_ENV === 'production' && process.env.POSTHOG_API_KEY ? process.env.POSTHOG_API_KEY : undefined;
  const posthogHost = process.env.POSTHOG_HOST || 'https://us.i.posthog.com';
  const installMs = clientDoc?.createdAt && (typeof (clientDoc.createdAt as any)?.toMillis === 'function'
    ? (clientDoc.createdAt as any).toMillis()
    : typeof clientDoc.createdAt === 'number' ? clientDoc.createdAt * 1000 : null);
  const posthogUser = user?.email && clientDoc ? {
    distinctId: user.email,
    shop_domain: clientDoc.shop ?? undefined,
    plan: clientDoc.plan ?? undefined,
    subscription_status: clientDoc.subscriptionStatus ?? undefined,
    days_since_install: installMs != null ? Math.floor((Date.now() - installMs) / (1000 * 60 * 60 * 24)) : undefined,
    total_orders: clientDoc.totalOrders ?? undefined,
    total_plays: clientDoc.totalPlays ?? undefined,
    games_created: clientDoc.gamesCreated ?? undefined,
    games_active: (clientDoc.gamesConfig && typeof clientDoc.gamesConfig === 'object' && Object.values(clientDoc.gamesConfig).filter((g: any) => g?.isActive).length) ?? undefined,
    selected_game: clientDoc.selectedGame ?? undefined,
    onboarding_completed: clientDoc.onboardingCompleted ?? undefined,
    widget_enabled: clientDoc.brandSettings?.widgetEnabled ?? undefined,
    checkout_extension_enabled: clientDoc.checkoutSettings?.enabled ?? undefined,
  } : undefined;

  console.log(`[loader] root ${pathname} ${Date.now() - __t0}ms`);
  return json({
    ENV: {
      NODE_ENV: process.env.NODE_ENV,
      GA4_MEASUREMENT_ID: process.env.GA4_MEASUREMENT_ID || null,
    },
    user,
    isPublicRoute,
    isNoLayoutRoute,
    hasAccountIssues,
    shouldPromptCheckoutExtension,
    shopDomain: clientDoc?.shop || null,
    posthogKey,
    posthogHost,
    posthogUser,
    locale,
  });
}

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const { ENV, user, isPublicRoute, isNoLayoutRoute, hasAccountIssues, posthogKey, posthogHost, posthogUser, locale } = useLoaderData<typeof loader>();
  const isRTL = locale === 'he';

  // PostHog client init and identify (production only). Runs for ALL visitors
  // — including logged-out marketing/SEO traffic — so we can measure the
  // visit -> register -> install funnel. identify() only fires for known users.
  useEffect(() => {
    if (ENV.NODE_ENV !== 'production' || !posthogKey) return;
    import('posthog-js').then(({ default: posthog }) => {
      // Disable automatic pageview here so we can drive SPA pageviews from the
      // Remix location below (Remix uses client-side navigation).
      posthog.init(posthogKey, { api_host: posthogHost, capture_pageview: false });
      if (posthogUser?.distinctId) {
        posthog.identify(posthogUser.distinctId, posthogUser);
      }
    }).catch(() => {});
  }, [ENV.NODE_ENV, posthogKey, posthogHost, posthogUser?.distinctId]);

  // Capture a $pageview on every route change (initial load + SPA navigation).
  useEffect(() => {
    if (ENV.NODE_ENV !== 'production' || !posthogKey) return;
    import('posthog-js').then(({ default: posthog }) => {
      posthog.capture('$pageview');
    }).catch(() => {});
  }, [ENV.NODE_ENV, posthogKey, location.pathname]);

  // Handle client-side navigation for authenticated users
  useEffect(() => {
    if (user && isPublicRoute) {
      // If user is authenticated and on a public route (like login/register),
      // redirect to dashboard or appropriate page
      if (location.pathname === '/login' || location.pathname === '/register') {
        navigate('/');
      }
    }
  }, [user, isPublicRoute, location.pathname, navigate]);

  // Inject production analytics (Google Ads gtag + GA4 + Microsoft Clarity) on
  // the client after hydration. Rendering these in the SSR <head> let them
  // mutate <head> before hydration and caused hydration mismatches; injecting
  // post-mount avoids that and keeps them off the critical render path.
  useEffect(() => {
    if (ENV.NODE_ENV !== 'production' || typeof document === 'undefined') return;
    const addInline = (id: string, code: string) => {
      if (document.getElementById(id)) return;
      const s = document.createElement('script');
      s.id = id;
      s.innerHTML = code;
      document.head.appendChild(s);
    };
    const addSrc = (id: string, src: string) => {
      if (document.getElementById(id)) return;
      const s = document.createElement('script');
      s.id = id;
      s.src = src;
      s.async = true;
      document.head.appendChild(s);
    };
    addSrc('ga-gtag-js', 'https://www.googletagmanager.com/gtag/js?id=AW-17530572341');
    addInline(
      'ga-gtag-init',
      `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','AW-17530572341');gtag('config','AW-18251969874');${
        ENV.GA4_MEASUREMENT_ID ? `gtag('config','${ENV.GA4_MEASUREMENT_ID}');` : ''
      }`
    );
    addInline(
      'ms-clarity',
      `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,"clarity","script","spjtn3lt6w");`
    );
  }, [ENV.NODE_ENV, ENV.GA4_MEASUREMENT_ID]);

  // Tidio chat — only on authenticated (non-public) routes, injected once.
  useEffect(() => {
    if (typeof document === 'undefined' || isPublicRoute) return;
    if (document.getElementById('tidio-chat-script')) return;
    const s = document.createElement('script');
    s.id = 'tidio-chat-script';
    s.src = '//code.tidio.co/pzl798ibas7tepncqlwed5w9vy061vtv.js';
    s.async = true;
    document.head.appendChild(s);
  }, [isPublicRoute]);

  return (
    <html lang={locale} dir={isRTL ? 'rtl' : 'ltr'} suppressHydrationWarning>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        {/* Third-party scripts (gtag, Clarity, Tidio) are injected client-side
            after mount via the effects in App() — NOT rendered here. They mutate
            <head> (e.g. Tidio adds a font preload) before React hydrates, which
            breaks document hydration; injecting them post-hydration avoids that
            and keeps them off the critical render path. */}
      </head>
      <body className="font-sans antialiased text-ink">
        <GamesLanguageProvider initialGamesLanguage={initializeGamesLanguage()}>
          <I18nProvider initialLocale={locale}>
            <RTLWrapper>
              {isPublicRoute || isNoLayoutRoute ? (
                <Outlet />
              ) : (
                <Layout hasAccountIssues={hasAccountIssues}>
                  <Outlet />
                </Layout>
              )}
            </RTLWrapper>
          </I18nProvider>
        </GamesLanguageProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default withSentry(App);

// Add ErrorBoundary to handle 404s and other errors without layout
export function ErrorBoundary() {
  const error = useRouteError();

  // Handle 404 errors without layout
  if (isRouteErrorResponse(error) && error.status === 404) {
    return (
      <html lang="en" dir="ltr" suppressHydrationWarning>
        <head>
          <meta charSet="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <Meta />
          <Links />
          {/* Google tag (gtag.js) - only in production */}
          {process.env.NODE_ENV === 'production' && (
            <>
              <script async src="https://www.googletagmanager.com/gtag/js?id=AW-17530572341"></script>
              <script
                dangerouslySetInnerHTML={{
                  __html: `
                    window.dataLayer = window.dataLayer || [];
                    function gtag(){dataLayer.push(arguments);} 
                    gtag('js', new Date());

                    gtag('config', 'AW-17530572341');
                  gtag('config', 'AW-18251969874');
                  `,
                }}
              />
            </>
          )}
          <title>404 - Page Not Found</title>
          {/* Microsoft Clarity tracking script - only in production */}
          {process.env.NODE_ENV === 'production' && (
            <script
              type="text/javascript"
              dangerouslySetInnerHTML={{
                __html: `
                  (function(c,l,a,r,i,t,y){
                      c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                      t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                      y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
                  })(window, document, "clarity", "script", "spjtn3lt6w");
                `,
              }}
            />
          )}
        </head>
        <body style={{ margin: 0, fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif' }}>
          <div style={{ minHeight: '100vh', width: '100%', background: 'linear-gradient(to bottom, #ffffff, #f3f4f6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <div style={{ width: '100%', maxWidth: 720, textAlign: 'center', background: 'rgba(255,255,255,0.95)', boxShadow: '0 20px 40px rgba(0,0,0,0.08)', borderRadius: 20, padding: 40, border: '1px solid #eef2f7' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img src={prizoLogo} alt="Prizo" style={{ height: 56, width: 'auto' }} />
              </div>
              <h1 style={{ marginTop: 24, fontSize: 72, fontWeight: 800, color: '#6d28d9', letterSpacing: '-0.02em' }}>404</h1>
              <h2 style={{ marginTop: 8, fontSize: 28, fontWeight: 700, color: '#111827' }}>Page Not Found</h2>
              <p style={{ marginTop: 8, fontSize: 16, color: '#4b5563' }}>
                Sorry, we couldn't find the page you're looking for.
              </p>
              <div style={{ marginTop: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
                <a href="/login" style={{ display: 'inline-flex', alignItems: 'center', color: '#ffffff', background: '#7c3aed', fontWeight: 600, padding: '12px 20px', borderRadius: 12, textDecoration: 'none', boxShadow: '0 8px 16px rgba(124,58,237,0.25)' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 8 }}>
                    <path d="M15 12H3"></path>
                    <path d="M9 18l-6-6 6-6"></path>
                  </svg>
                  Go to Login
                </a>
                <a href="/" style={{ display: 'inline-flex', alignItems: 'center', color: '#111827', background: '#e5e7eb', fontWeight: 600, padding: '12px 20px', borderRadius: 12, textDecoration: 'none' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 8 }}>
                    <path d="M10 19l-7-7"></path>
                    <path d="M3 12l7-7"></path>
                    <path d="M3 12h18"></path>
                  </svg>
                  Back to Home
                </a>
              </div>
              <div style={{ marginTop: 16, fontSize: 12, color: '#9ca3af' }}>Prizo © {new Date().getFullYear()}</div>
            </div>
          </div>
          <ScrollRestoration />
          <Scripts />
        </body>
      </html>
    );
  }

  captureRemixErrorBoundaryError(error);

  // For other errors, you might want to show a different layout
  // For now, let's also render them without layout
  return (
    <html lang="en" dir="ltr" suppressHydrationWarning>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
         {/* Google tag (gtag.js) - only in production */}
         {process.env.NODE_ENV === 'production' && (
           <>
             <script async src="https://www.googletagmanager.com/gtag/js?id=AW-17530572341"></script>
             <script
               dangerouslySetInnerHTML={{
                 __html: `
                   window.dataLayer = window.dataLayer || [];
                   function gtag(){dataLayer.push(arguments);} 
                   gtag('js', new Date());

                   gtag('config', 'AW-17530572341');
                  gtag('config', 'AW-18251969874');
                 `,
               }}
             />
           </>
         )}
                 <title>Error</title>
         {/* Microsoft Clarity tracking script - only in production */}
         {process.env.NODE_ENV === 'production' && (
           <script
             type="text/javascript"
             dangerouslySetInnerHTML={{
               __html: `
                 (function(c,l,a,r,i,t,y){
                     c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                     t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                     y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
                 })(window, document, "clarity", "script", "spjtn3lt6w");
               `,
             }}
           />
         )}
      </head>
      <body style={{ margin: 0, fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif' }}>
        <div style={{ minHeight: '100vh', width: '100%', background: 'linear-gradient(to bottom, #ffffff, #f3f4f6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ width: '100%', maxWidth: 640, textAlign: 'center', background: 'rgba(255,255,255,0.95)', boxShadow: '0 20px 40px rgba(0,0,0,0.08)', borderRadius: 16, padding: 32, border: '1px solid #eef2f7' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img src={prizoLogo} alt="Prizo" style={{ height: 56, width: 'auto' }} />
            </div>
            <h1 style={{ marginTop: 24, fontSize: 56, fontWeight: 800, color: '#ef4444' }}>Error</h1>
            <h2 style={{ marginTop: 12, fontSize: 24, fontWeight: 700, color: '#111827' }}>Something went wrong</h2>
            <p style={{ marginTop: 8, fontSize: 16, color: '#4b5563' }}>
              {error instanceof Error ? error.message : 'An unexpected error occurred'}
            </p>
          </div>
        </div>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}